<template>
  <div>
    <!-- 库位属性设置弹窗：按层编号展示该层全部库位，支持单行编辑与批量修改 -->
    <el-dialog title="库位设置" :visible.sync="dialogVisible" width="96%" top="4vh"
      :close-on-click-modal="false" @closed="reset">
      <div v-loading="loading" class="grid-setting">
        <!-- 顶部：当前层编号 + 批量操作入口 -->
        <div class="setting-head">
          <div><span class="label">层：</span>{{ floorNumber }}</div>
          <div class="batch-buttons">
            <el-button type="primary" size="small" @click="openBatchDialog('size')">批量修改尺寸</el-button>
            <el-button type="primary" size="small" @click="openBatchDialog('customer_type')">批量修改客户类型</el-button>
            <el-button type="primary" size="small" @click="openBatchDialog('operate_type')">批量修改操作类型</el-button>
            <el-button type="primary" size="small" @click="openBatchDialog('pack_type')">批量修改捆包类型</el-button>
            <el-button type="primary" size="small" @click="openBatchDialog('customer')">批量指定ID</el-button>
            <el-button type="primary" size="small" @click="openBatchDialog('remark')">批量修改备注</el-button>
          </div>
        </div>

        <!-- 库位列表：勾选后可用于批量操作 -->
        <el-table ref="gridTable" :data="grids" border height="58vh" @selection-change="handleSelectionChange" :header-cell-style="{ background: '#EDEFF4' }">
          <el-table-column type="selection" width="46" align="center" fixed />
          <!-- 库位按“货架备注-层-库位”展示 -->
          <el-table-column label="库位" min-width="150" fixed>
            <template slot-scope="scope">
              {{ formatFromGrid(scope.row) }}
            </template>
          </el-table-column>
          <!-- 长宽高，单位 cm，可留空 -->
          <el-table-column label="尺寸（cm）" min-width="330">
            <template slot-scope="scope">
              <div class="size-inputs">
                <el-input v-model.trim="scope.row.length" size="mini" placeholder="长" type="number" min="0" step="0.01" />
                <span>×</span>
                <el-input v-model.trim="scope.row.width" size="mini" placeholder="宽" type="number" min="0" step="0.01" />
                <span>×</span>
                <el-input v-model.trim="scope.row.height" size="mini" placeholder="高" type="number" min="0" step="0.01" />
              </div>
            </template>
          </el-table-column>
          <!-- 客户类型：0 通用 / 1 SVIP / 2 定额会员 / 3 普通客户 -->
          <el-table-column label="客户类型" min-width="135">
            <template slot-scope="scope">
              <el-select v-model="scope.row.customer_type" size="mini">
                <el-option v-for="item in customerTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </template>
          </el-table-column>
          <!-- 操作类型：0 通用 / 1 普货 / 2 FBA / 3 辅料 -->
          <el-table-column label="操作类型" min-width="125">
            <template slot-scope="scope">
              <el-select v-model="scope.row.operate_type" size="mini">
                <el-option v-for="item in operateTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </template>
          </el-table-column>
          <!-- 捆包类型：0 通用 / 1 非授权 / 2 授权装箱 -->
          <el-table-column label="捆包类型" min-width="135">
            <template slot-scope="scope">
              <el-select v-model="scope.row.pack_type" size="mini">
                <el-option v-for="item in packTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </template>
          </el-table-column>
          <!-- 指定 ID：0 通用 / 1 指定 ID -->
          <el-table-column label="指定ID" min-width="330">
            <template slot-scope="scope">
              <div class="customer-setting">
                <div class="customer-actions">
                  <el-radio-group v-model="scope.row.user_limit_type" size="mini" @change="changeUserLimit(scope.row)">
                    <el-radio v-for="item in userLimitTypeOptions" :key="item.value" :label="item.value">
                      {{ item.label }}
                    </el-radio>
                  </el-radio-group>
                  <el-button v-if="scope.row.user_limit_type === 1" type="primary" size="mini"
                    @click="openCustomerDialog(scope.row)">选择客户</el-button>
                </div>
                <div v-if="scope.row.user_limit_type === 1 && scope.row.user_ids.length" class="selected-customers">
                  <div class="selected-customers-title">已选客户ID</div>
                  <div v-for="id in scope.row.user_ids" :key="id" class="selected-customer-id">{{ id }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <!-- 自定义备注 -->
          <el-table-column label="自定义备注" min-width="220">
            <template slot-scope="scope">
              <el-input v-model="scope.row.remark" type="textarea" :rows="4" />
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </span>
    </el-dialog>

    <!-- 批量操作分别由子组件维护表单和校验，主组件只接收确认结果 -->
    <batchSizeSetting ref="batchSizeSetting" @confirm="applyBatchSize" />
    <batchOptionSetting ref="batchCustomerTypeSetting" title="批量修改客户类型" label="客户类型"
      :options="customerTypeOptions" @confirm="applyBatchCustomerType" />
    <batchOptionSetting ref="batchOperateTypeSetting" title="批量修改操作类型" label="操作类型"
      :options="operateTypeOptions" @confirm="applyBatchOperateType" />
    <batchOptionSetting ref="batchPackTypeSetting" title="批量修改捆包类型" label="捆包类型"
      :options="packTypeOptions" @confirm="applyBatchPackType" />
    <batchCustomerSetting ref="batchCustomerSetting" :options="userLimitTypeOptions"
      @confirm="applyBatchCustomer" />
    <batchRemarkSetting ref="batchRemarkSetting" @confirm="applyBatchRemark" />
    <!-- 单个库位与批量客户弹窗共用同一种客户 ID 搜索规则 -->
    <customerSelector ref="customerSelector" @confirm="setGridCustomers" />
  </div>
</template>

<script>
import batchSizeSetting from './batchSizeSetting'
import batchOptionSetting from './batchOptionSetting'
import batchCustomerSetting from './batchCustomerSetting'
import batchRemarkSetting from './batchRemarkSetting'
import customerSelector from './customerSelector'

export default {
  name: 'GridAttributeSetting',
  components: {
    batchSizeSetting,
    batchOptionSetting,
    batchCustomerSetting,
    batchRemarkSetting,
    customerSelector
  },
  data() {
    return {
      dialogVisible: false, // 弹窗显示状态
      loading: false, // 加载层详情
      saving: false, // 保存中
      floorNumber: '', // 当前编辑的层编号
      grids: [], // 当前层全部库位表单数据
      selectedGrids: [], // 表格已勾选的库位，用于批量操作
      customerTarget: null, // 当前正在选择客户的单行库位
      customerTypeOptions: [], // 客户类型选项
      operateTypeOptions: [], // 操作类型选项
      packTypeOptions: [], // 捆包类型选项
      userLimitTypeOptions: [] // 指定客户选项
    }
  },
  methods: {
    formatFromGrid(grid) {
      const grid_number = String(grid.grid_number).split('-')
      return `${[grid.shelf_remark, ...grid_number.slice(-2)].join('-')} `
    },
    // 打开设置弹窗，并按层编号加载该层全部库位属性
    open(row) {
      this.floorNumber = row.floor_number
      this.dialogVisible = true
      this.loading = true
      this.$api.warehouseApi.gridFloorDetail({ floor_number: this.floorNumber }).then(res => {
        const detail = res.data || {}
        const options = detail.options || {}
        const grids = Array.isArray(detail) ? detail : (detail.grids || detail.data)
        if (!Array.isArray(grids)) {
          this.$message.error('未找到该层的库位数据')
          this.dialogVisible = false
          return
        }
        this.customerTypeOptions = options.customer_type || []
        this.operateTypeOptions = options.operate_type || []
        this.packTypeOptions = options.pack_type || []
        this.userLimitTypeOptions = options.user_limit_type || []
        this.grids = grids.map(item => this.createGrid(item, detail.shelf_remark))
      }).finally(() => {
        this.loading = false
      })
    },
    // 将详情接口字段转为表单值，空尺寸继续保持可选状态
    createGrid(item, shelfRemark) {
      return {
        grid_number: item.grid_number,
        grid_short: item.grid_short,
        shelf_remark: shelfRemark,
        length: item.length == null ? '0' : item.length,
        width: item.width == null ? '0' : item.width,
        height: item.height == null ? '0' : item.height,
        customer_type: Number(item.customer_type || 0),
        operate_type: Number(item.operate_type || 0),
        pack_type: Number(item.pack_type || 0),
        user_limit_type: Number(item.user_limit_type || 0),
        user_ids: Array.isArray(item.user_ids) ? item.user_ids.map(Number) : [],
        remark: item.remark || ''
      }
    },
    // 同步表格勾选结果，供批量操作使用
    handleSelectionChange(rows) {
      this.selectedGrids = rows
    },
    // 所有批量操作都只作用于表格中已勾选的库位
    openBatchDialog(type) {
      if (!this.selectedGrids.length) {
        this.$message.warning('请先勾选要修改的库位')
        return
      }
      const refs = {
        size: 'batchSizeSetting',
        customer_type: 'batchCustomerTypeSetting',
        operate_type: 'batchOperateTypeSetting',
        pack_type: 'batchPackTypeSetting',
        customer: 'batchCustomerSetting',
        remark: 'batchRemarkSetting'
      }
      this.$refs[refs[type]].open()
    },
    // 批量回填尺寸
    applyBatchSize(form) {
      this.selectedGrids.forEach(item => {
        item.length = form.length
        item.width = form.width
        item.height = form.height
      })
    },
    // 批量回填客户类型
    applyBatchCustomerType(value) {
      this.applyBatchOption('customer_type', value)
    },
    // 批量回填操作类型
    applyBatchOperateType(value) {
      this.applyBatchOption('operate_type', value)
    },
    // 批量回填捆包类型
    applyBatchPackType(value) {
      this.applyBatchOption('pack_type', value)
    },
    // 批量回填下拉类字段
    applyBatchOption(field, value) {
      this.selectedGrids.forEach(item => { item[field] = value })
    },
    // 批量回填指定客户
    applyBatchCustomer(form) {
      this.selectedGrids.forEach(item => {
        item.user_limit_type = form.user_limit_type
        item.user_ids = form.user_limit_type === 1 ? form.user_ids.slice() : []
      })
    },
    // 批量回填备注
    applyBatchRemark(remark) {
      this.selectedGrids.forEach(item => { item.remark = remark })
    },
    // 切换为通用时清空已选客户 ID
    changeUserLimit(row) {
      if (row.user_limit_type === 0) row.user_ids = []
    },
    // 打开单行客户选择弹窗
    openCustomerDialog(row) {
      this.customerTarget = row
      this.$refs.customerSelector.open(row.user_ids)
    },
    // 将客户选择结果写回当前行
    setGridCustomers(userIds) {
      if (this.customerTarget) this.customerTarget.user_ids = userIds
    },
    // 尺寸允许留空；填写时必须为非负数且最多保留两位小数
    validateDimensions(list) {
      const fields = [
        { key: 'length', label: '长' },
        { key: 'width', label: '宽' },
        { key: 'height', label: '高' }
      ]
      for (let i = 0; i < list.length; i++) {
        for (let j = 0; j < fields.length; j++) {
          const value = list[i][fields[j].key]
          if (value !== '' && !/^\d+(\.\d{1,2})?$/.test(String(value))) {
            return `${fields[j].label}只能输入大于等于0的数字，最多两位小数`
          }
        }
      }
      return ''
    },
    // 校验后提交保存；可选尺寸为空时不发送对应参数
    save() {
      const dimensionError = this.validateDimensions(this.grids)
      if (dimensionError) return this.$message.error(dimensionError)
      const noCustomer = this.grids.find(item => item.user_limit_type === 1 && !item.user_ids.length)
      if (noCustomer) return this.$message.error(`${noCustomer.grid_number}请选择指定ID`)
      const grids = this.grids.map(item => {
        const grid = {
          grid_number: item.grid_number,
          customer_type: item.customer_type,
          operate_type: item.operate_type,
          pack_type: item.pack_type,
          user_limit_type: item.user_limit_type,
          remark: item.remark
        }
        if (item.length !== '') grid.length = Number(item.length)
        if (item.width !== '') grid.width = Number(item.width)
        if (item.height !== '') grid.height = Number(item.height)
        if (item.user_limit_type === 1) grid.user_ids = item.user_ids
        return grid
      })
      this.saving = true
      this.$api.warehouseApi.gridAttrSave({ floor_number: this.floorNumber, grids }).then(res => {
        this.$message.success(res.msg)
        this.dialogVisible = false
        this.$emit('saved')
      }).finally(() => {
        this.saving = false
      })
    },
    // 弹窗关闭后重置页面状态
    reset() {
      this.floorNumber = ''
      this.grids = []
      this.selectedGrids = []
      this.customerTarget = null
      this.customerTypeOptions = []
      this.operateTypeOptions = []
      this.packTypeOptions = []
      this.userLimitTypeOptions = []
      this.loading = false
      this.saving = false
    }
  }
}
</script>

<style lang="scss" scoped>
/* 表格区域最小高度，并固定表头 60px */
.grid-setting {
  min-height: 300px;

  /deep/ .el-table th {
    height: 60px;
    padding: 0;
  }

  /deep/ .el-table th > .cell {
    line-height: 60px;
  }
}

/* 顶部层编号与批量按钮 */
.setting-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .label {
    font-weight: 600;
  }
}

.batch-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .el-button {
    margin-left: 0;
  }
}

/* 尺寸输入：长 × 宽 × 高 */
.size-inputs {
  display: flex;
  align-items: center;
  gap: 6px;

  .el-input {
    width: 90px;
  }
}

/* 指定 ID：单选 + 选择按钮 + 已选客户列表 */
.customer-setting {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 10px 8px;

  /deep/.el-radio {
    margin-right: 16px;
  }
}

.customer-actions {
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
}

.selected-customers {
  width: 110px;
  border: 1px solid #dcdfe6;
  text-align: center;
}

.selected-customers-title,
.selected-customer-id {
  min-height: 22px;
  padding: 2px 6px;
  line-height: 18px;
}

.selected-customers-title {
  background: #f5f7fa;
  border-bottom: 1px solid #dcdfe6;
}

.selected-customer-id + .selected-customer-id {
  border-top: 1px solid #ebeef5;
}

</style>
